import React, { useState } from "react";
import axios from "axios";
import { useSearchParams } from "react-router-dom";
import {
  Container,
  Card,
  Row,
  Col,
  Dropdown,
  OverlayTrigger,
  Tooltip,
  Button,
  Spinner,
} from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";

import {
  onSetCustomerNumberPreference,
  setIsContactDataSaved,
} from "../../store/slices/contactSlice";
import {
  setShowErrorModal,
  setShowSuccessModal,
} from "../../store/slices/preferenceSlice";
import { getUrl } from "../../helpers/getUrl";

import SetupFieldsDropdown from "../SetupFieldsDropdown";
import { Loading } from "../Loading";

import arrowIcon from "../../assets/images/arrowIcon.svg";

import { useCmsHook } from "../../helpers/cmsHook";

function ContactSyncRules() {
  const { cmsInstance } = useCmsHook();

  const {
    hubspot: {
      hubspotDropdownFields: {
        customerSearchFields: hsFilterFields,
        customer: hsCustomerNumberField,
      },
      loading: hsLoading,
    },
    tripletex: {
      tripletexDropdownFields: { customerSearchFields: txFilterFields },
      loading: txLoading,
    },
  } = useSelector((state) => state.mapping);

  const {
    txFilterPreference,
    hubspotFilterPreference,
    hubspotCustomerNumberPreference,
    txCustomerNumberPreference,
    customerNumberPreference,
    customerNumberFields,
    txCustomerNumberDropdownField,
  } = useSelector((state) => state.contact);

  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);

  const toggleCustomerNumberText = (selectedValue) => {
    return customerNumberFields.find((item) => item.value === selectedValue)
      .label;
  };

  return (
    <div className="sync-settings-setup-page">
      {hsLoading || txLoading || !cmsInstance ? (
        <Loading />
      ) : (
        <Container className="px-0">
          <Card className="sync-settings-card" style={{ marginTop: "29px" }}>
            <Card.Body className="sync-settings-card-body">
              {cmsInstance &&
                cmsInstance.showRichText(
                  "cmsContent.contacts.contactSyncRules.block1"
                )}
              {hsFilterFields &&
                hsFilterFields.length > 0 &&
                txFilterFields &&
                txFilterFields.length > 0 && (
                  <Row className="mt-3">
                    <Col sm={5} lg={3}>
                      <b>HubSpot</b>
                      <SetupFieldsDropdown
                        fieldsData={hsFilterFields}
                        dropdownFor="hubspotFilterFields"
                        selectedPreference={hubspotFilterPreference}
                      />
                    </Col>
                    <Col
                      sm={1}
                      lg={1}
                      className="mx-0 sync-settings-card-direction"
                    >
                      <div>
                        <img src={arrowIcon} alt="arrow-icon" />
                      </div>
                    </Col>
                    <Col sm={5} lg={3}>
                      <b>Tripletex</b>
                      <SetupFieldsDropdown
                        fieldsData={txFilterFields}
                        dropdownFor="txFilterFields"
                        selectedPreference={txFilterPreference}
                      />
                    </Col>
                  </Row>
                )}
            </Card.Body>
          </Card>

          <Card className="sync-settings-card">
            <Card.Body className="sync-settings-card-body">
              <div>
                {cmsInstance &&
                  cmsInstance.showRichText(
                    "cmsContent.contacts.contactSyncRules.block2.text"
                  )}

                <Dropdown className="my-3">
                  <Dropdown.Toggle className="sync-settings-card-dropdown">
                    {toggleCustomerNumberText(customerNumberPreference.value)}
                  </Dropdown.Toggle>

                  <Dropdown.Menu>
                    {customerNumberFields &&
                      customerNumberFields.map((item) => {
                        return (
                          <Dropdown.Item
                            key={item.label}
                            onClick={() => {
                              dispatch(
                                onSetCustomerNumberPreference({
                                  selectedOption: item.value,
                                })
                              );
                            }}
                          >
                            {item.label}
                          </Dropdown.Item>
                        );
                      })}
                  </Dropdown.Menu>
                </Dropdown>
                {customerNumberPreference.value === "tx" &&
                  cmsInstance &&
                  cmsInstance.showRichText(
                    "cmsContent.contacts.contactSyncRules.block2.createCustomer"
                  )}
                {customerNumberPreference.value === "hubspot" &&
                  cmsInstance &&
                  cmsInstance.showRichText(
                    "cmsContent.contacts.contactSyncRules.block2.takeCustomer"
                  )}
              </div>
            </Card.Body>
          </Card>

          <Card className="sync-settings-card">
            <Card.Body className="sync-settings-card-body">
              {customerNumberPreference.value === "tx" &&
                cmsInstance &&
                cmsInstance.showRichText(
                  "cmsContent.contacts.contactSyncRules.block3.createCustomer"
                )}
              {customerNumberPreference.value === "hubspot" &&
                cmsInstance &&
                cmsInstance.showRichText(
                  "cmsContent.contacts.contactSyncRules.block3.takeCustomer"
                )}
              {hsCustomerNumberField &&
                hsCustomerNumberField.length > 0 &&
                txCustomerNumberDropdownField &&
                txCustomerNumberDropdownField.length > 0 && (
                  <Row className="mt-3">
                    <Col sm={5} lg={3}>
                      <b>HubSpot</b>
                      <SetupFieldsDropdown
                        fieldsData={hsCustomerNumberField}
                        dropdownFor="hsCustomerNumberField"
                        selectedPreference={hubspotCustomerNumberPreference}
                      />
                    </Col>

                    <Col sm={1} className="mx-0 sync-settings-card-direction">
                      <div>
                        <img
                          src={arrowIcon}
                          alt="arrow-icon"
                          style={{
                            transform:
                              customerNumberPreference.value === "tx"
                                ? "scaleX(-1)"
                                : "",
                          }}
                        />
                      </div>
                    </Col>

                    <Col sm={5} lg={3}>
                      <b>Tripletex</b>
                      <SetupFieldsDropdown
                        fieldsData={txCustomerNumberDropdownField}
                        dropdownFor="txCustomerNumberField"
                        selectedPreference={txCustomerNumberPreference}
                        isDisabled={true}
                      />
                    </Col>
                  </Row>
                )}
            </Card.Body>
          </Card>

          <div
            className="d-flex flex-row justify-content-end mb-4"
            style={{ marginTop: "27px" }}
          >
            {!hubspotFilterPreference ||
            !txFilterPreference ||
            !hubspotCustomerNumberPreference ? (
              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip style={{ fontSize: "12px" }}>
                    {showToolTipText(
                      hubspotFilterPreference,
                      txFilterPreference,
                      hubspotCustomerNumberPreference
                    )}
                  </Tooltip>
                }
              >
                <Button className="save-changes-disabled-button">Save</Button>
              </OverlayTrigger>
            ) : (
              <Button
                className="save-changes-button"
                onClick={() =>
                  onSaveContactSyncRules({
                    setIsLoading,
                    searchParams,
                    hubspotFilterPreference,
                    txFilterPreference,
                    hubspotCustomerNumberPreference,
                    customerNumberPreference,
                    dispatch,
                  })
                }
              >
                {isLoading ? (
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                  />
                ) : (
                  <span>Save</span>
                )}
              </Button>
            )}
          </div>
        </Container>
      )}
    </div>
  );
}

export default ContactSyncRules;

const showToolTipText = (
  hubspotFilterPreference,
  txFilterPreference,
  hubspotCustomerNumberPreference
) => {
  if (!hubspotFilterPreference) {
    return "The search parameter for HubSpot has not been selected. Please select it before proceeding.";
  } else if (!txFilterPreference) {
    return "The search parameter for Tripletex has not been selected. Please select it before proceeding.";
  } else if (!hubspotCustomerNumberPreference) {
    return "HubSpot customer number preference is not selected. Please select it before proceeding.";
  }
};

export const onSaveContactSyncRules = async ({
  setIsLoading,
  searchParams,
  hubspotFilterPreference,
  txFilterPreference,
  hubspotCustomerNumberPreference,
  customerNumberPreference,
  dispatch,
}) => {
  try {
    setIsLoading(true);

    const portalId = searchParams.get("portalId");
    await axios.post(getUrl("REACT_APP_SAVE_PREFERENCES"), {
      portalId,
      preferences: {
        filterOptions: {
          filterHsProperty: hubspotFilterPreference,
          filterTxProperty: txFilterPreference,
        },
        hubspotCustomerNumber: {
          direction: customerNumberPreference.value,
          mapping: {
            hubspotProperty: hubspotCustomerNumberPreference,
          },
        },
      },
      preferenceType: "customer",
    });
    dispatch(setIsContactDataSaved({ tab: "syncRules" }));
    dispatch(
      setShowSuccessModal({
        message:
          "cmsContent.notifications.syncSettings.successContent.contactSyncRules.message",
      })
    );
  } catch (error) {
    console.log(error);
    let errorMessage;
    if (error.response && error.response.data.message)
      errorMessage = error.response.data.message;
    else errorMessage = error.message;
    console.log(errorMessage);
    dispatch(setShowErrorModal({ message: errorMessage }));
  } finally {
    setIsLoading(false);
  }
};
